// Catalog.ID Client — Shamir's Secret Sharing over GF(2⁸)
// ============================================================================
// Splits the 44-byte random portion of the extended supersecret into n shares
// with threshold k, per Recovery Kit Addendum §4.1. Each secret byte is split
// independently with a fresh random polynomial of degree k−1 whose constant
// term is the byte; any k distinct (x, y) points reconstruct it via Lagrange
// interpolation at x = 0, and fewer than k reveal nothing (information-
// theoretic). x = 0 is never used as a share index — that point IS the secret.
//
// Field: GF(2⁸) with the AES reduction polynomial x⁸+x⁴+x³+x+1 (0x11b),
// generator 3. Addition is XOR; multiplication via log/exp tables.

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
    let x = 1;
    for (let i = 0; i < 255; i++) {
        EXP[i] = x;
        LOG[x] = i;
        x ^= (x << 1) ^ (x & 0x80 ? 0x11b : 0);
        x &= 0xff;
    }
    // Duplicate so exp lookups of summed logs (< 510) need no modulo.
    for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
}

function gfMul(a: number, b: number): number {
    if (a === 0 || b === 0) return 0;
    return EXP[LOG[a] + LOG[b]];
}

function gfDiv(a: number, b: number): number {
    if (b === 0) throw new Error('GF(2^8) division by zero');
    if (a === 0) return 0;
    return EXP[LOG[a] + 255 - LOG[b]];
}

export interface SssShare {
    /** x-coordinate, 1..255. Equal across all physical copies of a share type. */
    index: number;
    /** y-coordinates, one byte per secret byte. */
    data: Uint8Array;
}

/**
 * Draw the non-constant coefficients for a `k`-threshold split of a
 * `byteCount`-byte secret: `byteCount × (k−1)` CSPRNG bytes laid out
 * **byte-major** — for secret byte `i` the coefficients c₁…c_{k−1} in ascending
 * degree, at offset `i × (k−1)`.
 *
 * Split out of {@link sssSplit} for the Recovery Kit's stored kit entry
 * (registration-restructure §16.4): the coefficients ARE the set, so keeping
 * them lets any sheet be re-evaluated — reprinted — later. Nothing else about
 * the share depends on when it was drawn.
 *
 * TRAP (§16.9): coefficients are bound to ONE secret. Re-evaluating the same
 * coefficients under a rotated secret publishes `s_new ⊕ s_old` to anyone
 * holding one old and one new sheet of the same index. A new secret means a new
 * draw, always.
 */
export function sssCoefficients(k: number, byteCount: number): Uint8Array {
    if (!Number.isInteger(k)) throw new Error('k must be an integer');
    if (k < 2) throw new Error('Threshold k must be at least 2');
    if (!Number.isInteger(byteCount) || byteCount <= 0) throw new Error('Secret must not be empty');
    return crypto.getRandomValues(new Uint8Array(byteCount * (k - 1)));
}

/**
 * Evaluate the polynomials `secret` + `coefficients` define at each of
 * `indexes`, one share per index in the order given.
 *
 * `coefficients` is the byte-major block {@link sssCoefficients} produces; its
 * length fixes the threshold (`k = coefficients.length / secret.length + 1`),
 * so a block of the wrong size is a hard error rather than a silently different
 * threshold.
 *
 * Pure: the same `(secret, coefficients, index)` always yields the same bytes.
 * That is what makes a reprinted Recovery Sheet combine with the one printed at
 * registration (§16.4).
 */
export function sssEvaluate(
    secret: Uint8Array, coefficients: Uint8Array, indexes: number[],
): SssShare[] {
    if (secret.length === 0) throw new Error('Secret must not be empty');
    if (coefficients.length === 0 || coefficients.length % secret.length !== 0) {
        throw new Error(
            `Coefficient block of ${coefficients.length} bytes is not a whole multiple of the `
            + `${secret.length}-byte secret`);
    }
    const degree = coefficients.length / secret.length; // = k − 1
    for (const x of indexes) {
        if (!Number.isInteger(x) || x < 1 || x > 255) {
            throw new Error(`Invalid share index ${x} — indexes are 1..255 (x = 0 IS the secret)`);
        }
    }

    return indexes.map(index => {
        const data = new Uint8Array(secret.length);
        for (let byteIdx = 0; byteIdx < secret.length; byteIdx++) {
            const base = byteIdx * degree;
            // Horner from the highest degree down to the constant term.
            let y = coefficients[base + degree - 1];
            for (let j = degree - 2; j >= 0; j--) {
                y = gfMul(y, index) ^ coefficients[base + j];
            }
            data[byteIdx] = gfMul(y, index) ^ secret[byteIdx];
        }
        return { index, data };
    });
}

/**
 * Split `secret` into `n` shares with threshold `k`.
 * Share indexes are 1..n.
 *
 * The composition of {@link sssCoefficients} and {@link sssEvaluate}: a caller
 * that needs the set again later keeps the coefficients and calls the pair
 * directly (§16.4).
 */
export function sssSplit(secret: Uint8Array, k: number, n: number): SssShare[] {
    if (!Number.isInteger(k) || !Number.isInteger(n)) throw new Error('k and n must be integers');
    if (k < 2) throw new Error('Threshold k must be at least 2');
    if (n < k) throw new Error('Total shares n must be >= threshold k');
    if (n > 255) throw new Error('At most 255 shares (GF(2^8) x-coordinates)');
    if (secret.length === 0) throw new Error('Secret must not be empty');

    const coefficients = sssCoefficients(k, secret.length);
    try {
        return sssEvaluate(secret, coefficients, Array.from({ length: n }, (_, i) => i + 1));
    } finally {
        coefficients.fill(0);
    }
}

/**
 * Reconstruct the secret from `shares` (must be >= the original threshold k;
 * passing exactly k distinct shares is the normal case). Indexes must be
 * distinct — duplicates throw. Passing fewer shares than the threshold does
 * NOT throw (the count isn't encoded in the shares themselves) — it returns
 * garbage; callers must validate the result (recovery-kit.ts checks base57
 * alphabet membership).
 */
export function sssCombine(shares: SssShare[]): Uint8Array {
    if (shares.length < 2) throw new Error('At least 2 shares are required');
    const length = shares[0].data.length;
    const seen = new Set<number>();
    for (const s of shares) {
        if (s.index < 1 || s.index > 255) throw new Error(`Invalid share index ${s.index}`);
        if (seen.has(s.index)) throw new Error(`Duplicate share index ${s.index}`);
        if (s.data.length !== length) throw new Error('Shares have mismatched lengths');
        seen.add(s.index);
    }

    // Lagrange basis at x = 0: L_i(0) = Π_{j≠i} x_j / (x_j ⊕ x_i).
    // In GF(2^k) subtraction is XOR, so no sign bookkeeping is needed.
    const basis = shares.map((si, i) => {
        let num = 1;
        let den = 1;
        for (let j = 0; j < shares.length; j++) {
            if (j === i) continue;
            num = gfMul(num, shares[j].index);
            den = gfMul(den, shares[j].index ^ si.index);
        }
        return gfDiv(num, den);
    });

    const secret = new Uint8Array(length);
    for (let byteIdx = 0; byteIdx < length; byteIdx++) {
        let acc = 0;
        for (let i = 0; i < shares.length; i++) {
            acc ^= gfMul(shares[i].data[byteIdx], basis[i]);
        }
        secret[byteIdx] = acc;
    }
    return secret;
}
